'use client';

import {
  AnimatedDiv,
  checkIsNative,
  Div,
  H5,
  P,
  Span,
  useHandleBack,
  useKeyboardHeight,
  useKeyboardShown,
  useSafeAreaInsets,
  useWindowSize,
} from '@scatterlab/zeta-core';
import {
  memo,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import Button from '../button/Button';
import { Warning16Icon } from '../icons';
import Input from '../Input';

export type PopupProps = {
  message: string;
  description?: string;
  subDescription?: string;
  primaryButtonText: string;
  children?: ReactNode;
  primaryButtonDisabled?: boolean | ((value?: string) => boolean);
  onPressPrimary: (value: string) => string | void | Promise<string | void>;
  secondaryButtonText?: string;
  secondaryButtonDisabled?: boolean | ((value?: string) => boolean);
  onPressSecondary?: (value: string) => void;
  closeable?: boolean;
  onClose: (reason: 'primary' | 'secondary' | 'close', value?: string) => void;
  maxLength?: number;
  placeholder?: string;
  zIndex: number;
  type?: 'default' | 'form';
  formLabel?: string;
  defaultValue?: string;
  submitOnEnter?: boolean;
  clearFormButton?: boolean;
};

const Popup = ({
  message,
  description,
  subDescription,
  children,
  primaryButtonText,
  primaryButtonDisabled,
  onPressPrimary,
  secondaryButtonText,
  secondaryButtonDisabled,
  onPressSecondary,
  closeable,
  onClose,
  maxLength,
  placeholder,
  zIndex,
  type = 'default',
  formLabel,
  defaultValue,
  submitOnEnter,
  clearFormButton,
}: PopupProps) => {
  const [value, setValue] = useState<string>(defaultValue ?? '');
  const [error, setError] = useState<string | undefined>();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePressPrimary = useCallback(async () => {
    try {
      if (isSubmitting) return;
      setIsSubmitting(true);
      const errorMessage = await onPressPrimary(value);
      if (errorMessage) {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [isSubmitting, onPressPrimary, value]);

  useEffect(() => {
    if (typeof window === 'undefined' || !('addEventListener' in window))
      return undefined;
    const handlePopstate = (e: PopStateEvent) => {
      e.preventDefault();
      onClose('close');
    };

    window.addEventListener('popstate', handlePopstate);
    return () => {
      window.removeEventListener('popstate', handlePopstate);
    };
  }, [onClose]);

  const handleBack = useCallback(() => {
    onClose('close');
  }, [onClose]);

  useHandleBack(handleBack);

  const keyboardHeight = useKeyboardHeight();
  const keyboardShown = useKeyboardShown();
  const { top } = useSafeAreaInsets();
  const { height } = useWindowSize();

  const containerHeight = useMemo(() => {
    if (!checkIsNative()) return undefined;

    if (keyboardShown && keyboardHeight !== 0) {
      return height - keyboardHeight + top;
    }
    return '100%';
  }, [height, keyboardHeight, top, keyboardShown]);

  return (
    <AnimatedDiv
      className="native:absolute fixed inset-0 w-full bg-black/60 backdrop-blur-16"
      style={{
        zIndex,
        height: containerHeight,
      }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onPress={closeable ? () => onClose('close') : undefined}
    >
      <Div
        className="absolute left-1/2 top-1/2 flex w-[343px] -translate-x-1/2 -translate-y-1/2 flex-col items-center rounded-xl border-white/5 bg-gray-main px-[14px] pb-4 pt-[32px] shadow-400"
        onPress={(e) => e.stopPropagation()}
      >
        {children || (
          <>
            <H5 className="title16 whitespace-pre-line text-center text-white">
              {message}
            </H5>
            {description && (
              <P className="body14 mt-4 whitespace-pre-line break-keep text-center text-gray-300">
                {description}
              </P>
            )}
            {subDescription && (
              <Div className="body12 mt-4 whitespace-pre-line break-keep text-center text-white/50">
                {subDescription}
              </Div>
            )}
          </>
        )}
        {type === 'form' && (
          <Div className="mt-4 flex w-full flex-col items-start gap-2">
            {formLabel && <Span className="body14">{formLabel}</Span>}
            <Input
              value={value}
              onChangeText={setValue}
              maxLength={maxLength}
              placeholder={placeholder}
              onSubmitEditing={submitOnEnter ? handlePressPrimary : undefined}
              className="w-full"
              clearButton={clearFormButton}
            />
            {error && (
              <Div className="flex flex-row items-center gap-1">
                <Warning16Icon className="size-4 text-primary-400" />
                <Span className="caption12 text-primary-300">{error}</Span>
              </Div>
            )}
          </Div>
        )}
        <Div className="mt-6 flex w-full flex-row gap-2">
          {secondaryButtonText && (
            <Button
              variant="secondary"
              size="medium"
              className="flex-1"
              onPress={() => onPressSecondary?.(value)}
              disabled={
                typeof secondaryButtonDisabled === 'boolean'
                  ? secondaryButtonDisabled
                  : secondaryButtonDisabled?.(value)
              }
              type="button"
            >
              {secondaryButtonText}
            </Button>
          )}
          <Button
            variant="primary"
            size="medium"
            onPress={handlePressPrimary}
            className="flex-1"
            disabled={
              (typeof primaryButtonDisabled === 'boolean'
                ? primaryButtonDisabled
                : primaryButtonDisabled?.(value)) || isSubmitting
            }
            type="button"
          >
            {primaryButtonText}
          </Button>
        </Div>
      </Div>
    </AnimatedDiv>
  );
};

export default memo(Popup);
